(() => {
  if (customElements.get('home-landing')) return;

  const catalogs = new Map();
  const getCatalog = (path) => {
    let endpoint;
    try {
      endpoint = new URL(path, location.origin);
      if (endpoint.origin !== location.origin) return Promise.resolve([]);
    } catch (_) {
      return Promise.resolve([]);
    }
    if (!catalogs.has(endpoint.href)) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      catalogs.set(endpoint.href, fetch(endpoint.href, {
        signal: controller.signal, credentials: 'same-origin', mode: 'same-origin', headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error('Catalog unavailable');
        return response.json();
      }).then((data) => {
        if (data.ok !== true || !Array.isArray(data.releases)) return [];
        const seen = new Set();
        return data.releases.filter((release) => typeof release?.coverUrl === 'string')
          .sort((a, b) => (Date.parse(b.releaseDate) || 0) - (Date.parse(a.releaseDate) || 0))
          .flatMap((release) => {
            try {
              const url = new URL(release.coverUrl, location.origin);
              if (url.protocol !== 'https:' || url.username || url.password) return [];
              // Only Shopify's image CDN supports this transformation; preserve other URLs.
              if (url.hostname === 'cdn.shopify.com' ||
                ((url.origin === location.origin || url.hostname.endsWith('.myshopify.com')) &&
                  url.pathname.startsWith('/cdn/shop/'))) url.searchParams.set('width', '320');
              url.hash = '';
              if (seen.has(url.href)) return [];
              seen.add(url.href);
              return [url.href];
            } catch (_) {
              return [];
            }
          });
      }).catch(() => []).finally(() => clearTimeout(timeout)));
    }
    return catalogs.get(endpoint.href);
  };

  const loadCover = (source, signal) => new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      signal.removeEventListener('abort', cancel);
      image.onload = image.onerror = null;
      resolve(result);
    };
    const cancel = () => {
      finish(null);
      image.removeAttribute('src');
    };
    const timeout = setTimeout(cancel, 8000);
    signal.addEventListener('abort', cancel, { once: true });
    if (signal.aborted) { cancel(); return; }
    image.className = 'er-home-landing__cover';
    image.alt = '';
    image.width = 320;
    image.height = 320;
    image.decoding = 'async';
    image.draggable = false;
    image.onload = async () => {
      try { if (image.decode) await image.decode(); } catch (_) { /* A loaded image can still render. */ }
      finish(image.naturalWidth ? image : null);
    };
    image.onerror = () => finish(null);
    image.src = source;
  });

  class HomeLanding extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.dataset.liveCovers === 'false') return;
      this.grid = this.querySelector('.er-home-landing__grid');
      if (!this.grid) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;
      this.rows = [];
      this.covers = [];
      this.failed = new Set();
      this.imagePromises = new Map();
      this.images = new Map();
      this.revealQueue = [];
      this.offset = 0;
      this.cursor = 0;
      this.columns = 0;
      this.rowCount = 0;
      this.step = 0;
      this.visible = !('IntersectionObserver' in window);
      this.catalogSettled = false;
      this.ready = false;
      this.classList.remove('is-ready');
      const content = this.querySelector('.er-home-landing__content');
      this.entrance = Promise.allSettled((content?.getAnimations?.({ subtree: true }) || [])
        .filter((animation) => animation.animationName === 'aura-element-enter')
        .map((animation) => animation.finished));
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => this.updateMotion(), { signal });
      document.addEventListener('visibilitychange', () => this.updateMotion(), { signal });
      this.tick = (time) => this.animate(time);
      this.resize = () => {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => this.layout());
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.resize);
        this.resizeObserver.observe(this);
      } else window.addEventListener('resize', this.resize, { signal });
      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.updateMotion();
        });
        this.intersection.observe(this);
      }
      this.layout();
      getCatalog(this.dataset.catalogUrl).then((covers) => {
        if (!this.isConnected || this.generation !== generation) return;
        this.covers = covers;
        this.catalogSettled = true;
        this.populateRows();
        this.updateMotion();
      });
    }

    disconnectedCallback() {
      this.generation += 1;
      this.buildVersion = (this.buildVersion || 0) + 1;
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersection?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.layoutFrame);
      clearTimeout(this.revealTimer);
      this.revealTimer = null;
      this.revealQueue = [];
      this.frame = null;
      this.style.setProperty('--landing-tile-motion', 'paused');
      this.rows = [];
    }

    layout() {
      if (!this.isConnected) return;
      const width = this.grid.getBoundingClientRect().width;
      if (!width) return;
      this.style.setProperty('--landing-render-height', `${this.clientHeight}px`);
      const probe = document.createElement('div');
      probe.className = 'er-home-landing__tile';
      probe.style.cssText = 'position:absolute;visibility:hidden;';
      this.grid.append(probe);
      const size = probe.getBoundingClientRect().width;
      probe.remove();
      if (!size) return;
      const styles = getComputedStyle(this);
      const step = size + (parseFloat(styles.getPropertyValue('--landing-gap')) || 0);
      this.speed = Math.max(1, Math.min(40, parseFloat(styles.getPropertyValue('--landing-speed')) || 12));
      this.offset = this.step ? this.offset / this.step * step : 0;
      this.step = step;
      const columns = Math.ceil(width / step) + 2;
      const rowCount = Math.max(1, Math.min(12, Math.round(parseFloat(styles.getPropertyValue('--landing-rows')) || 5)));
      this.positionRows();
      if (this.columns === columns && this.rowCount === rowCount) return;
      this.columns = columns;
      this.rowCount = rowCount;
      this.buildRows(columns, rowCount);
    }

    buildRows(columns, rowCount) {
      this.buildVersion = (this.buildVersion || 0) + 1;
      clearTimeout(this.revealTimer);
      this.revealTimer = null;
      this.revealQueue = [];
      const rows = Array.from({ length: rowCount }, (_, rowIndex) => {
        const row = document.createElement('div');
        row.className = 'er-home-landing__row';
        const track = document.createElement('div');
        track.className = 'er-home-landing__track';
        for (let column = 0; column < columns; column += 1) {
          const tile = document.createElement('div');
          tile.className = 'er-home-landing__tile';
          let variation = Math.imul(rowIndex + 1, 73856093) ^ Math.imul(column + 1, 19349663);
          variation = Math.imul(variation ^ (variation >>> 16), 0x45d9f3b);
          variation = (variation ^ (variation >>> 16)) >>> 0;
          tile.dataset.palette = String(variation % 8 + 1);
          tile.style.setProperty('--landing-tile-angle', `${variation % 360}deg`);
          tile.style.setProperty('--landing-tile-x', `${15 + (variation >>> 8) % 71}%`);
          tile.style.setProperty('--landing-tile-y', `${15 + (variation >>> 16) % 71}%`);
          tile.style.setProperty('--landing-tile-tint', `${((variation >>> 24) % 4 === 0 ? 65 : 25) + (variation >>> 12) % 21}%`);
          tile.style.setProperty('--landing-tile-phase', `${-((variation >>> 3) % 6500) / 1000}s`);
          track.append(tile);
        }
        row.append(track);
        return { row, track, right: rowIndex % 2 === 1, next: null, loading: false };
      });
      this.rows = rows;
      this.cursor = columns * rowCount;
      this.revealInterval = Math.max(6, Math.min(30, 1400 / this.cursor));
      this.positionRows();
      this.grid.replaceChildren(...rows.map((row) => row.row));
      this.classList.add('has-grid');
      this.populateRows();
      this.updateMotion();
    }

    getImage(source) {
      if (!this.imagePromises.has(source)) {
        const generation = this.generation;
        this.imagePromises.set(source, loadCover(source, this.controller.signal).then((image) => {
          if (this.generation === generation) {
            if (image) this.images.set(source, image);
            else {
              this.failed.add(source);
              if (this.failed.size === this.covers.length) this.updateMotion();
            }
          }
          return image;
        }));
      }
      return this.imagePromises.get(source);
    }

    populateRows() {
      if (!this.covers.length) return;
      const version = this.buildVersion;
      const entrance = this.entrance;
      this.rows.forEach((row, rowIndex) => {
        Array.from(row.track.children).forEach((tile, column) => {
          const source = this.covers[(column * this.rowCount + rowIndex) % this.covers.length];
          tile.coverSource = source;
          const tileVersion = tile.coverVersion = (tile.coverVersion || 0) + 1;
          if (this.ready && this.images.has(source)) {
            this.mountCover(tile, this.images.get(source), false);
            return;
          }
          this.getImage(source).then(async (image) => {
            if (!image) {
              if (this.isConnected && version === this.buildVersion && tile.coverVersion === tileVersion) tile.classList.add('is-unavailable');
              return;
            }
            await entrance;
            if (!this.isConnected || version !== this.buildVersion || tile.coverVersion !== tileVersion) return;
            const job = { tile, row, source, image, version, tileVersion };
            if (this.motion.matches || navigator.connection?.saveData) {
              this.revealCover(job, false);
              return;
            }
            this.revealQueue.splice(Math.floor(Math.random() * (this.revealQueue.length + 1)), 0, job);
            this.scheduleReveal();
          });
        });
      });
    }

    mountCover(tile, image, animate) {
      const cover = image.cloneNode();
      if (!animate) cover.classList.add('is-settled');
      tile.replaceChildren(cover);
      tile.classList.remove('is-unavailable');
      tile.classList.add('is-loaded');
    }

    revealCover({ tile, row, source, image, version, tileVersion }, animate) {
      if (!this.isConnected || version !== this.buildVersion || tile.parentNode !== row.track || tile.coverSource !== source || tile.coverVersion !== tileVersion) return;
      this.mountCover(tile, image, animate);
      if (!this.ready) {
        this.ready = true;
        this.classList.add('is-ready');
        this.updateMotion();
      }
    }

    scheduleReveal() {
      if (!this.revealTimer && this.revealQueue.length) {
        this.revealTimer = setTimeout(() => this.revealNext(), this.revealInterval);
      }
    }

    revealNext() {
      this.revealTimer = null;
      const job = this.revealQueue.shift();
      if (job) this.revealCover(job, !this.motion.matches && !navigator.connection?.saveData);
      this.scheduleReveal();
    }

    updateMotion() {
      if (this.motion.matches || navigator.connection?.saveData) {
        clearTimeout(this.revealTimer);
        this.revealTimer = null;
        const pending = this.revealQueue.splice(0);
        pending.forEach((job) => this.revealCover(job, false));
      }
      const enabled = this.dataset.animate === 'true' && !this.motion.matches && !navigator.connection?.saveData;
      const waiting = !this.catalogSettled || this.covers.length > this.failed.size;
      this.style.setProperty('--landing-tile-motion', this.isConnected && enabled && this.visible && !document.hidden && waiting ? 'running' : 'paused');
      this.running = this.isConnected && this.rows.length > 0 && this.step > 0 && enabled && this.visible && !document.hidden && waiting;
      if (this.running) {
        this.rows.forEach((row) => this.prepareNext(row));
        if (!this.frame) {
          this.lastTime = null;
          this.frame = requestAnimationFrame(this.tick);
        }
      } else {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
    }

    prepareNext(row) {
      if (!this.running || !this.ready || !this.covers.length || row.loading || row.next || this.failed.size >= this.covers.length) return;
      let source;
      do { source = this.covers[this.cursor++ % this.covers.length]; } while (this.failed.has(source));
      row.loading = true;
      this.getImage(source).then((image) => {
        if (!this.isConnected || !this.rows.includes(row)) return;
        row.loading = false;
        if (image) row.next = { image, source };
        else {
          this.failed.add(source);
          this.prepareNext(row);
        }
      });
    }

    positionRows() {
      this.rows.forEach((row) => {
        row.track.style.transform = `translate3d(${row.right ? this.offset - this.step : -this.offset}px,0,0)`;
      });
    }

    animate(time) {
      this.frame = null;
      if (!this.running || !this.isConnected) return;
      const delta = this.lastTime === null ? 0 : Math.min(64, time - this.lastTime);
      this.lastTime = time;
      this.offset += this.speed * delta / 1000;
      if (this.offset >= this.step) {
        this.offset -= this.step;
        this.rows.forEach((row) => {
          const recycled = row.right ? row.track.lastElementChild : row.track.firstElementChild;
          if (row.right) row.track.prepend(recycled);
          else row.track.append(recycled);
          // The recycled tile is outside the clipped row in either direction.
          if (row.next) {
            recycled.coverSource = row.next.source;
            recycled.coverVersion = (recycled.coverVersion || 0) + 1;
            this.mountCover(recycled, row.next.image, false);
            row.next = null;
          }
          this.prepareNext(row);
        });
      }
      this.positionRows();
      this.frame = requestAnimationFrame(this.tick);
    }
  }

  customElements.define('home-landing', HomeLanding);
})();
